/**
 * FASE B (H3-FULL) — Analytics Team REAL.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Users, Trophy, Zap, ClipboardList, Loader2, Filter,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_OPTIONS = [
  { v: "today", l: "Oggi" },
  { v: "wtd",   l: "Sett." },
  { v: "mtd",   l: "Mese" },
  { v: "last30", l: "30gg" },
  { v: "ytd",   l: "Anno" },
];


function Initials({ name }) {
  const letters = (name || "?").split(" ")
    .filter(Boolean).slice(0, 2)
    .map((s) => s[0].toUpperCase()).join("");
  return (
    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#163321] to-[#0F1F14] text-white flex items-center justify-center text-sm font-heading">
      {letters}
    </div>
  );
}

function Sparkline({ data = [], color = "#163321" }) {
  const max = Math.max(1, ...data);
  const w = 90, h = 26;
  const step = w / Math.max(1, data.length - 1);
  const points = data.map((v, i) =>
    `${(i * step).toFixed(1)},${(h - (v / max) * (h - 2) - 1).toFixed(1)}`,
  ).join(" ");
  return (
    <svg width={w} height={h} className="ml-auto">
      <polyline fill="none" stroke={color} strokeWidth={1.5} points={points} />
    </svg>
  );
}

function KPIItem({ label, value, color = "text-[#0F1F14]" }) {
  return (
    <div>
      <div className={`font-heading text-xl ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}

function TeamMemberCard({ m, onClick }) {
  const conv = m.conversion_rate;
  const convStr = conv === null || conv === undefined ? "—" : `${Math.round(conv * 100)}%`;
  const convColor = conv === null ? "text-slate-400"
    : conv >= 0.5 ? "text-emerald-700"
    : conv >= 0.25 ? "text-amber-700"
    : "text-red-700";

  return (
    <button
      type="button" onClick={onClick}
      className="text-left w-full bg-white border border-[#E5E2DB] rounded-2xl p-4 hover:border-[#163321]/40 hover:shadow-md transition-all"
      data-testid={`team-card-${m.user_id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <Initials name={m.name} />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-base text-[#0F1F14] truncate">{m.name}</div>
          <div className="text-[11px] text-slate-500 font-mono truncate">{m.email}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">{m.role}</div>
        </div>
        {m.tasks_open > 0 && (
          <div className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1">
            <ClipboardList className="w-3 h-3" /> {m.tasks_open}
          </div>
        )}
      </div>
      <div className="grid grid-cols-4 gap-2 mb-3">
        <KPIItem label="Aperte" value={m.assigned_open ?? 0} />
        <KPIItem label="Chiuse OK" value={m.closed_positive ?? 0} color="text-emerald-700" />
        <KPIItem label="Conv." value={convStr} color={convColor} />
        <KPIItem label="Ø chiusura" value={m.avg_closure_days ? `${m.avg_closure_days}g` : "—"} />
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-[#E5E2DB]">
        <div className="text-[10px] text-slate-500">Attività 30gg</div>
        <Sparkline data={m.activity_last_30d || []} />
      </div>
    </button>
  );
}

function TopBadge({ icon: Icon, label, member, valueLabel, color }) {
  if (!member) return null;
  return (
    <div className="bg-gradient-to-br from-white to-[#F7F5EE] border-2 border-[#B9A268]/30 rounded-2xl p-4"
          data-testid={`ranking-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-5 h-5 ${color}`} />
        <span className="text-xs uppercase tracking-wider text-slate-600 font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <Initials name={member.name} />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-base text-[#0F1F14] truncate">{member.name}</div>
          <div className="text-xs text-slate-500">{valueLabel}</div>
        </div>
      </div>
    </div>
  );
}


export default function AdminTeamAnalyticsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = searchParams.get("period") || "mtd";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/kpi/team-performance?period=${period}`,
                { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period]);

  const setPeriod = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("period", p);
    setSearchParams(next);
  };

  const ranking = data?.ranking || {};
  const isSelfScope = data?.self_scope === true;

  const totalAssigned = useMemo(() =>
    (data?.team || []).reduce((s, m) => s + (m.assigned_open || 0), 0),
    [data]);

  return (
    <div className="p-6 max-w-full" data-testid="admin-team-analytics-page">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin/kpi"
                 className="text-sm text-slate-500 hover:text-[#0F1F14] inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-4 h-4" /> KPI Analytics
          </Link>
          <h1 className="font-heading text-3xl text-[#0F1F14] flex items-center gap-2">
            <Users className="w-7 h-7 text-[#B9A268]" />
            Analytics Team
          </h1>
          {isSelfScope && (
            <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
              <Filter className="w-3 h-3" /> Visualizzazione self-scope (solo i tuoi dati)
            </p>
          )}
        </div>
        <div className="flex gap-1 bg-[#F7F5EE] p-1 rounded-full">
          {PERIOD_OPTIONS.map((p) => (
            <button
              key={p.v} type="button"
              onClick={() => setPeriod(p.v)}
              className={`px-3 py-1 text-xs rounded-full transition-all ${
                period === p.v ? "bg-[#163321] text-white" : "text-slate-600 hover:bg-white"
              }`}
              data-testid={`period-${p.v}`}
            >
              {p.l}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="text-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 mx-auto animate-spin" />
          <p className="mt-2 text-sm">Caricamento KPI…</p>
        </div>
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="bg-white border border-[#E5E2DB] rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Team attivo</div>
              <div className="font-heading text-2xl text-[#0F1F14]">{data?.team?.length ?? 0}</div>
            </div>
            <div className="bg-white border border-[#E5E2DB] rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Richieste aperte totali</div>
              <div className="font-heading text-2xl text-[#0F1F14]">{totalAssigned}</div>
            </div>
            <TopBadge icon={Trophy} label="Top closer" color="text-amber-600"
                       member={ranking.top_closer}
                       valueLabel={ranking.top_closer
                         ? `${ranking.top_closer.closed_positive} chiuse positive` : ""} />
            <TopBadge icon={Zap} label="Più veloce" color="text-sky-700"
                       member={ranking.fastest}
                       valueLabel={ranking.fastest
                         ? `Ø ${ranking.fastest.avg_closure_days}g/chiusura` : ""} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(data?.team || []).map((m) => (
              <TeamMemberCard key={m.user_id} m={m}
                                onClick={() => setSelectedMember(m)} />
            ))}
          </div>

          {(!data?.team || data.team.length === 0) && (
            <div className="bg-white border border-[#E5E2DB] rounded-2xl p-10 text-center text-slate-400 italic">
              Nessun dipendente attivo nel team.
            </div>
          )}
        </>
      )}

      {selectedMember && (
        <MemberDrillDown m={selectedMember} onClose={() => setSelectedMember(null)} />
      )}
    </div>
  );
}


function MemberDrillDown({ m, onClose }) {
  const brk = m.breakdown_by_ramo || {};
  const rows = [
    ["Convenzioni RCA", brk.convenzioni_leads || 0],
    ["D&O",             brk.do_quotations || 0],
    ["Cyber",           brk.cyber_questionari || 0],
    ["Fine Arts",       brk.fine_arts_pratiche || 0],
    ["Famiglia/Viaggi", brk.richieste_assicurative || 0],
  ];
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="team-drilldown-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Initials name={m.name} />
            <div>
              <div className="font-heading text-xl text-[#0F1F14]">{m.name}</div>
              <div className="text-xs text-slate-500 font-mono">{m.email}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">{m.role}</div>
            </div>
          </div>
          <button type="button" onClick={onClose}
                    className="text-slate-500 hover:text-[#0F1F14] px-2 py-1 text-xl"
                    data-testid="drilldown-close">✕</button>
        </div>

        <div className="grid grid-cols-4 gap-3 mb-5">
          <div className="p-3 bg-[#F7F5EE] rounded-xl text-center">
            <div className="font-heading text-2xl text-[#0F1F14]">{m.assigned_open}</div>
            <div className="text-[10px] uppercase text-slate-500">Aperte</div>
          </div>
          <div className="p-3 bg-emerald-50 rounded-xl text-center">
            <div className="font-heading text-2xl text-emerald-700">{m.closed_positive}</div>
            <div className="text-[10px] uppercase text-slate-500">Chiuse+</div>
          </div>
          <div className="p-3 bg-red-50 rounded-xl text-center">
            <div className="font-heading text-2xl text-red-700">{m.closed_negative}</div>
            <div className="text-[10px] uppercase text-slate-500">Chiuse−</div>
          </div>
          <div className="p-3 bg-white border rounded-xl text-center">
            <div className="font-heading text-2xl text-[#0F1F14]">
              {m.conversion_rate ? `${Math.round(m.conversion_rate * 100)}%` : "—"}
            </div>
            <div className="text-[10px] uppercase text-slate-500">Conversion</div>
          </div>
        </div>

        <div className="mb-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Breakdown per ramo</div>
          <div className="space-y-1">
            {rows.map(([lbl, v]) => (
              <div key={lbl} className="flex items-center justify-between text-sm px-3 py-1.5 bg-[#F7F5EE] rounded-lg">
                <span>{lbl}</span>
                <span className="font-semibold text-[#0F1F14]">{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="p-3 bg-white border rounded-xl">
            <div className="text-[10px] uppercase text-slate-500 mb-0.5">Ø primo contatto</div>
            <div className="font-heading text-lg">{m.avg_first_contact_days ? `${m.avg_first_contact_days}g` : "—"}</div>
          </div>
          <div className="p-3 bg-white border rounded-xl">
            <div className="text-[10px] uppercase text-slate-500 mb-0.5">Ø chiusura</div>
            <div className="font-heading text-lg">{m.avg_closure_days ? `${m.avg_closure_days}g` : "—"}</div>
          </div>
          <div className="p-3 bg-white border rounded-xl">
            <div className="text-[10px] uppercase text-slate-500 mb-0.5">Task aperti</div>
            <div className="font-heading text-lg">{m.tasks_open}</div>
          </div>
          <div className="p-3 bg-white border rounded-xl">
            <div className="text-[10px] uppercase text-slate-500 mb-0.5">Task chiusi (periodo)</div>
            <div className="font-heading text-lg">{m.tasks_closed_period}</div>
          </div>
        </div>

        <Link
          to={`/admin/richieste?assigned_to=${encodeURIComponent(m.user_id)}`}
          className="mt-5 block w-full text-center bg-[#163321] text-white py-2.5 rounded-xl hover:bg-[#0F1F14] text-sm font-medium"
          data-testid="drilldown-view-richieste"
        >
          Vedi richieste assegnate →
        </Link>
      </div>
    </div>
  );
}
